import type { RoomState } from "../types";
import { PlayerAvatar } from "./PlayerAvatar";

interface Props {
  room: RoomState;
  you: string;
  onReady: (ready: boolean) => void;
  onStart: () => void;
  onKick: (id: string) => void;
}

export function Lobby({ room, you, onReady, onStart, onKick }: Props) {
  const me = room.players.find((p) => p.id === you);
  const isHost = room.hostId === you;
  const enoughPlayers = room.players.length >= 2;
  const allReady = enoughPlayers && room.players.every((p) => p.ready || p.id === room.hostId);

  return (
    <div className="lobby">
      <div className="lobby-hero">
        <h1 className="lobby-title">Draw the cat. Win the votes.</h1>
        <p className="lobby-sub">
          Everyone gets the same random cat photo. Sixty seconds. No mercy. The lobby
          decides who painted the truest little guy.
        </p>
      </div>

      <div className="lobby-grid">
        <section className="card">
          <div className="card-head">
            <h2>Lobby</h2>
            <span className="muted">{room.players.length}/8 players</span>
          </div>
          <ul className="player-list">
            {room.players.map((p) => (
              <li key={p.id} className={p.ready ? "is-ready" : ""}>
                <PlayerAvatar player={p} />
                <div className="player-meta">
                  <div className="player-name">
                    {p.name}
                    {p.id === room.hostId && <span className="pill pill-host">host</span>}
                    {p.cosmetics.catnipPack && <span className="pill pill-gold">catnip</span>}
                  </div>
                  <div className="player-status">
                    {p.id === room.hostId ? "starts the round" : p.ready ? "ready" : "lacing up..."}
                  </div>
                </div>
                {isHost && p.id !== you && (
                  <button className="ghost danger" onClick={() => onKick(p.id)}>
                    kick
                  </button>
                )}
              </li>
            ))}
            {Array.from({ length: Math.max(0, 4 - room.players.length) }).map((_, i) => (
              <li key={`empty-${i}`} className="empty-slot">
                <div className="avatar avatar-empty">?</div>
                <div className="player-meta muted">Waiting for a friend...</div>
              </li>
            ))}
          </ul>
        </section>

        <section className="card card-action">
          <h2>How to play</h2>
          <ol className="rules">
            <li>A random cat photo appears for everyone in the channel.</li>
            <li>You get <b>60 seconds</b> to capture its essence.</li>
            <li>Vote for the best rendition (you can't vote for yourself).</li>
            <li>Three rounds. Highest score wins bragging rights.</li>
          </ol>

          {isHost ? (
            <button
              className="primary big"
              onClick={onStart}
              disabled={!enoughPlayers}
              title={!enoughPlayers ? "Need at least 2 players" : ""}
            >
              {enoughPlayers ? "Start the round" : "Waiting for players..."}
            </button>
          ) : (
            <button
              className={me?.ready ? "secondary big" : "primary big"}
              onClick={() => onReady(!me?.ready)}
            >
              {me?.ready ? "Hold up, not ready" : "I'm ready"}
            </button>
          )}

          {isHost && enoughPlayers && !allReady && (
            <p className="muted small">You can start any time, but it's classier to wait.</p>
          )}
        </section>
      </div>
    </div>
  );
}
